import React, { FC, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import DatePickerDropdown from 'app/components/datepicker-dropdown';
import DatePickerPresets from 'app/components/datepicker-dropdown/DatePickerPresets';
import {
  getDatesFromPreset,
  getDatesWithConstraints,
  isDateWithinRange,
} from 'app/components/datepicker-dropdown/utils';
import useSetState from 'utils/hooks/useSetState';
import { getRangePlaceholderLabel } from 'app/components/datepicker-dropdown/utils/placeholder';
import { DatePreset } from 'app/components/datepicker-dropdown/enums';

const MAX_DATE = dayjs().endOf('day');

const DATE_PRESETS = [DatePreset.THIS_MONTH, DatePreset.LAST_MONTH];

type Props = {
  initialPreset: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
  onChange: (
    {
      startDate,
      endDate,
    }: {
      startDate: Dayjs | null;
      endDate: Dayjs | null;
    },
    preset?: string,
  ) => void;
  dataName?: Record<string, string>;
};

const InsightsLiteTopBarDatePicker: FC<Props> = ({
  initialPreset,
  startDate,
  endDate,
  onChange,
  dataName,
}) => {
  const [preset, setPreset] = useState<string | undefined>(
    initialPreset ?? undefined,
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useSetState<{
    startDate: boolean;
    endDate: boolean;
  }>({
    startDate: true,
    endDate: true,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [startDateTemp, setStartDateTemp] = useState<Dayjs | null>(null);
  const [endDateTemp, setEndDateTemp] = useState<Dayjs | null>(null);

  const handlePresetSelect = (preset?: string) => {
    setPreset(preset);

    if (preset) {
      const presetDates = getDatesWithConstraints(getDatesFromPreset(preset), {
        maxEndDate: MAX_DATE,
      });

      onChange(presetDates, preset);
      setIsOpen(false);
      return;
    }

    onChange(
      {
        startDate,
        endDate,
      },
      undefined,
    );
  };

  const onRangeSelect = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;

    setStartDateTemp(start ? dayjs(start) : null);
    setEndDateTemp(end ? dayjs(end) : null);
  };

  const selectedDate = startDate ?? null;

  useEffect(() => {
    if (startDate && endDate) {
      setError({
        startDate: startDate
          ? isDateWithinRange(startDate, null, MAX_DATE)
          : false,
        endDate: endDate ? isDateWithinRange(endDate, null, MAX_DATE) : false,
      });
    }
  }, [startDate, endDate, setError]);

  return (
    <DatePickerDropdown
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      label={getRangePlaceholderLabel({
        startDateTemp: startDateTemp ?? startDate,
        endDateTemp: endDateTemp ?? endDate,
      })}
      selected={selectedDate?.toDate() ?? null}
      startDate={startDateTemp?.toDate() ?? null}
      endDate={endDateTemp?.toDate() ?? null}
      selectsRange={false}
      dateFormat={'MM/yyyy'}
      hideDefaultDatePicker={true}
      maxDate={MAX_DATE.toDate()}
      onChange={(val) => {
        onRangeSelect(val as [Date | null, Date | null]);
      }}
      refreshKey={`${startDateTemp?.toString()}${endDateTemp?.toString()}-${startDate?.toString()}${endDate?.toString()}`}
    >
      <DatePickerPresets
        presets={DATE_PRESETS}
        onPresetSelect={handlePresetSelect}
        selectedPreset={preset}
      />
    </DatePickerDropdown>
  );
};

export default InsightsLiteTopBarDatePicker;
